import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toValidObjectIdString, formatApiDate } from '../utils/security';
import { fetchHakedisler, fetchHakedisDetay } from '../services/hakedisService';
import HakedisDetayModal from '../components/HakedisDetayModal';
import TableListFooter from '../components/TableListFooter';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

const DURUM_LABELS = {
  OnayBekliyor: 'Onay Bekliyor',
  FaturaBekleyen: 'Fatura Bekleyen',
  Tamamlandi: 'Tamamlandı',
  IptalEdildi: 'İptal Edildi',
};

const getHakedisId = (h) => toValidObjectIdString(h?.id ?? h?._id) || '';

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const DURUM_VALUES = ['', 'OnayBekliyor', 'FaturaBekleyen', 'Tamamlandi', 'IptalEdildi'];

const HakedislerPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const durumFromUrl = searchParams.get('durum') || '';
  const initialDurum = DURUM_VALUES.includes(durumFromUrl) ? durumFromUrl : '';
  const aramaFromUrl = searchParams.get('arama') || '';

  const [hakedisler, setHakedisler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(aramaFromUrl);
  const [debouncedSearch, setDebouncedSearch] = useState(aramaFromUrl);
  const [durumFilter, setDurumFilter] = useState(initialDurum);
  const [selectedHakedis, setSelectedHakedis] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput.trim()) next.set('arama', searchInput.trim());
        else next.delete('arama');
        return next;
      }, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput, setSearchParams]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (initialDurum) next.set('durum', initialDurum);
      if (aramaFromUrl) next.set('arama', aramaFromUrl);
      return next;
    }, { replace: true });
  }, [initialDurum, aramaFromUrl, setSearchParams]);

  const effectiveArama = debouncedSearch.length >= 3 ? debouncedSearch : undefined;

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, durumFilter]);

  useEffect(() => {
    if (paginationMeta.totalPages >= 1 && currentPage > paginationMeta.totalPages) {
      setCurrentPage(paginationMeta.totalPages);
    }
  }, [paginationMeta.totalPages, currentPage]);

  const loadHakedisler = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        sayfa: currentPage,
        limit: PAGE_SIZE,
        arama: effectiveArama,
      };
      if (durumFilter) params.durum = durumFilter;
      const response = await fetchHakedisler(params);
      if (response?.basarili && response?.veri) {
        setHakedisler(response.veri.hakedisler || []);
        const p = response.veri.sayfalama || {};
        setPaginationMeta({
          total: p.toplam ?? 0,
          totalPages: Math.max(1, p.toplamSayfa ?? 1),
        });
      } else {
        setHakedisler([]);
        setPaginationMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Hakedişler yüklenemedi'));
      setHakedisler([]);
      setPaginationMeta({ total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [durumFilter, effectiveArama, currentPage]);

  useEffect(() => {
    loadHakedisler();
  }, [loadHakedisler]);

  const handleViewDetail = async (hakedis) => {
    const id = getHakedisId(hakedis);
    if (!id) {
      window.showNotification?.('error', 'Hakediş ID alınamadı');
      return;
    }
    try {
      const response = await fetchHakedisDetay(id);
      if (response?.basarili && response?.veri) {
        setSelectedHakedis(response.veri.hakedis);
        setDetailModalOpen(true);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Detay yüklenemedi'));
    }
  };

  return (
    <main className="main-content hakedisler-page">
      <div className="content-header">
        <h1>Hakedişler</h1>
      </div>

      <div className="admin-finance-toolbar">
        <div className="admin-finance-search-row">
          <div className="search-box admin-finance-search-box">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Hakediş no veya teknik servis (en az 3 karakter)…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              maxLength={80}
              aria-label="Ara"
            />
            {loading && searchInput.trim().length >= 3 && (
              <i className="fas fa-spinner fa-spin admin-finance-search-spinner" aria-hidden="true"></i>
            )}
          </div>
          <select
            className="admin-finance-select"
            value={durumFilter}
            onChange={(e) => {
              const v = e.target.value;
              setDurumFilter(v);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (v) next.set('durum', v);
                else next.delete('durum');
                return next;
              }, { replace: true });
            }}
            aria-label="Durum filtresi"
          >
            <option value="">Tümü</option>
            <option value="OnayBekliyor">Onay Bekliyor</option>
            <option value="FaturaBekleyen">Fatura Bekleyen</option>
            <option value="Tamamlandi">Tamamlandı</option>
            <option value="IptalEdildi">İptal Edildi</option>
          </select>
        </div>
        <p className="admin-finance-search-hint">
          Arama en az 3 karakter sonrası uygulanır. Sayfa başına en fazla {PAGE_SIZE} kayıt.
        </p>
      </div>

      <div className="card">
        {loading && hakedisler.length === 0 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <div className="loading-text">Hakedişler yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state">{error}</div>
        ) : hakedisler.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-file-invoice-dollar" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }}></i>
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>Hakediş bulunamadı</div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Hakediş No</th>
                    <th>Teknik Servis</th>
                    <th>Tarih</th>
                    <th style={{ textAlign: 'right' }}>Tutar</th>
                    <th>Durum</th>
                    <th className="islemler-sutunu">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {hakedisler.map((h, idx) => (
                    <tr key={getHakedisId(h) || idx}>
                      <td style={{ fontWeight: 500 }}>{h.numara || '—'}</td>
                      <td>{h.teknikServisAdi || '—'}</td>
                      <td>{formatApiDate(h.olusturmaTarihi)}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(h.toplamTutar)}</td>
                      <td>{DURUM_LABELS[h.durum] || h.durum}</td>
                      <td className="table-actions islemler-sutunu" style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="category-action-btn edit-btn"
                          onClick={() => handleViewDetail(h)}
                          title="Detay"
                        >
                          <i className="fas fa-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {detailModalOpen && selectedHakedis && (
        <HakedisDetayModal
          hakedis={selectedHakedis}
          onClose={() => {
            setDetailModalOpen(false);
            setSelectedHakedis(null);
          }}
          onRefresh={loadHakedisler}
        />
      )}
    </main>
  );
};

export default HakedislerPage;
